<template>
  <div style="position: relative; overflow: visible;">
    <canvas ref="canvas" :style="{ width: '100%', height: height + 'px' }" />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import { Chart, BarController, BarElement, LinearScale, CategoryScale, Tooltip } from 'chart.js';

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip);

const props = defineProps({
  data: { type: Array, default: () => [] },
  buyData: { type: Array, default: () => [] },
  sellData: { type: Array, default: () => [] },
  height: { type: Number, default: 38 },
  totalNet: { type: Number, default: 0 },
});

const canvas = ref(null);
let chartInstance = null;

const shortNum = (v) => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '+';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(1) + 'K';
  return sign + '$' + Math.round(abs);
};

function externalTooltip(context) {
  const { chart, tooltip } = context;
  let el = chart.canvas.parentNode.querySelector('.nf-tooltip');

  if (!el) {
    el = document.createElement('div');
    el.className = 'nf-tooltip';
    chart.canvas.parentNode.appendChild(el);
  }

  if (tooltip.opacity === 0) {
    el.style.opacity = '0';
    el.style.pointerEvents = 'none';
    return;
  }

  const title = tooltip.title[0] || '';
  const raw = tooltip.dataPoints?.[0]?.raw;
  const labelData = tooltip.dataPoints?.[0]?.dataset ? (function() {
    const i = tooltip.dataPoints[0].dataIndex;
    return { net: props.data[i] || 0, buy: props.buyData[i] || 0, sell: props.sellData[i] || 0 };
  })() : { net: 0, buy: 0, sell: 0 };

  const netClass = labelData.net >= 0 ? 'color:#22c55e' : 'color:#ef4444';
  const totalClass = props.totalNet >= 0 ? 'color:#22c55e' : 'color:#ef4444';

  el.innerHTML = `
    <div style="font-size:10px;opacity:0.5;margin-bottom:3px">${title}</div>
    <div style="display:flex;justify-content:space-between;gap:12px"><span style="opacity:0.5">Buy</span><span style="color:#22c55e;font-family:monospace">${shortNum(labelData.buy)}</span></div>
    <div style="display:flex;justify-content:space-between;gap:12px"><span style="opacity:0.5">Sell</span><span style="color:#ef4444;font-family:monospace">${shortNum(-labelData.sell)}</span></div>
    <div style="display:flex;justify-content:space-between;gap:12px"><span style="opacity:0.5">1h Net</span><span style="${netClass};font-family:monospace">${shortNum(labelData.net)}</span></div>
    <div style="height:1px;background:rgba(139,92,246,0.15);margin:4px 0"></div>
    <div style="display:flex;justify-content:space-between;gap:12px"><span style="opacity:0.5">24h Net</span><span style="${totalClass};font-family:monospace;font-weight:600">${shortNum(props.totalNet)}</span></div>
  `;

  // Position tooltip above the bar, clamped to viewport edges
  el.style.opacity = '1';
  el.style.pointerEvents = 'none';
  el.style.top = '-8px';
  el.style.transform = 'translateY(-100%)';

  // Set left first to measure, then clamp
  el.style.left = tooltip.caretX + 'px';
  el.style.transform = 'translate(-50%, -100%)';

  // Check if tooltip overflows viewport right edge
  const canvasRect = chart.canvas.getBoundingClientRect();
  const tipRect = el.getBoundingClientRect();
  const viewportW = window.innerWidth;

  if (tipRect.right > viewportW - 8) {
    // Shift left so it fits
    const overflow = tipRect.right - viewportW + 8;
    el.style.left = (tooltip.caretX - overflow) + 'px';
    el.style.transform = 'translate(-50%, -100%)';
  }
  if (tipRect.left < 8) {
    el.style.left = (tooltip.caretX + (8 - tipRect.left)) + 'px';
    el.style.transform = 'translate(-50%, -100%)';
  }
}

function render() {
  if (!canvas.value || !props.data || props.data.length < 2) return;

  const points = props.data;
  const colors = points.map(v => v >= 0 ? '#22c55e' : '#ef4444');
  const hoverColors = points.map(v => v >= 0 ? '#16a34a' : '#dc2626');

  if (chartInstance) chartInstance.destroy();

  chartInstance = new Chart(canvas.value, {
    type: 'bar',
    data: {
      labels: points.map((_, i) => {
        const h = points.length - 1 - i;
        return h === 0 ? 'Now' : h + 'h ago';
      }),
      datasets: [{
        data: points,
        backgroundColor: colors,
        hoverBackgroundColor: hoverColors,
        borderWidth: 0,
        borderRadius: 1,
        barPercentage: 0.85,
        categoryPercentage: 0.9,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false, // disable canvas tooltip, use external HTML
          external: externalTooltip,
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => items[0]?.label || '',
            label: (ctx) => {
              const i = ctx.dataIndex;
              return { net: props.data[i] || 0, buy: props.buyData[i] || 0, sell: props.sellData[i] || 0 };
            },
          },
        },
      },
      scales: {
        x: { display: false },
        y: { display: false, beginAtZero: false },
      },
      animation: { duration: 0 },
      layout: { padding: 0 },
    }
  });
}

onMounted(() => nextTick(render));
watch(() => props.data, () => nextTick(render), { deep: true });
</script>

<style>
.nf-tooltip {
  position: absolute;
  z-index: 1000;
  background: rgba(26, 26, 46, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  transition: opacity 0.15s;
  pointer-events: none;
}
</style>
